<script setup>
// ============================================================================
// BonBoZ: THANH TRÊN — chuyển giữa các phần lớn của app (Hội thoại · Thống kê ·
// Cài đặt), đúng bố cục Pancake và Retion đang dùng.
//
// Vì sao đổi: Chatwoot nhét cả hai việc vào một thanh trái — vừa chuyển phần
// lớn, vừa lọc hội thoại. Người trực page ở trong "Hội thoại" gần như cả ngày,
// nên thanh trái nên dành hết cho việc lọc; những thứ mở vài lần một tuần
// (thống kê, cài đặt) đưa lên thanh trên, ra khỏi tầm mắt lúc đang trực.
//
// Thanh trái vẫn còn nguyên và thu lại thành RAIL biểu tượng: Chatwoot tự thu
// gọn khi rộng dưới 160px (components-next/sidebar/provider.js), hover vào một
// mục là bung popover các mục con — không mất đường vào đâu cả.
// ============================================================================
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { chuDeHienTai, timChuDe } from 'dashboard/helper/chuDe';
import UnreadBadge from 'next/Conversation/ConversationCard/UnreadBadge.vue';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';
import ChuongThongBao from 'dashboard/components-next/sidebar/SidebarNotificationBell.vue';
import MergedPagesMenu from 'dashboard/components/MergedPagesMenu.vue';

// Chuyen tiep len Dashboard.vue de mo bang phim tat — cung mot modal ma thanh
// trai dang goi, khong dung ban thu hai.
const emit = defineEmits(['openKeyShortcutModal']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { isAdmin } = useAdmin();
// Thanh trai da nap va lam moi so nay (Sidebar.vue + actionCable), chi doc lai.
// Can co feature flag `conversation_unread_counts` bat tren tai khoan.
const tongChuaDoc = useMapGetter('conversationUnreadCounts/getAllUnreadCount');
const chuaDocTheoTrang = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const { selectedInboxIds, isAllSelected } = useSelectedPages();

// Badge phai dem dung pham vi dang nhin: loc vai trang thi cong so cua may
// trang do, khong lay tong ca tai khoan.
const soChuaDoc = computed(() => {
  if (isAllSelected.value) return tongChuaDoc.value;
  return selectedInboxIds.value.reduce(
    (tong, id) => tong + chuaDocTheoTrang.value(id),
    0
  );
});
// KHONG hien ten/o chuyen tai khoan: account la khai niem da dich vu cua
// Chatwoot (mot nguoi thuoc nhieu workspace), BonBoZ dung dung MOT tai khoan
// nen bay ten ra chi sinh cau hoi 'Acme Inc la gi'. Da thu hien moi ten cho
// gon - van bi hoi (17/08/2026) -> bo han. Can doi tai khoan (hiem): keo rong
// thanh trai cua Chatwoot ra la o chuyen hien lai o do.

// Mỗi mục kèm các tiền tố đường dẫn để biết đang đứng ở phần nào. Dùng đường
// dẫn thay vì liệt kê tên route: Chatwoot có vài chục tên route cho cùng một
// phần, liệt kê thiếu một cái là mục đó không sáng lên mà chẳng ai biết vì sao.
const { checkPermissions } = usePolicy();

const CAC_MUC = computed(() => {
  const goc = `/app/accounts/${accountId.value}`;
  return [
    {
      ten: 'hoi-thoai',
      nhan: t('BONBOZ.THANH_TREN.HOI_THOAI'),
      icon: 'i-lucide-messages-square',
      toi: { name: 'home', params: { accountId: accountId.value } },
      thuoc: [
        `${goc}/dashboard`,
        `${goc}/conversations`,
        `${goc}/inbox`,
        `${goc}/team`,
        `${goc}/label`,
        `${goc}/mentions`,
        `${goc}/unattended`,
        `${goc}/folder`,
      ],
    },
    {
      ten: 'bao-cao',
      chiAdmin: true,
      nhan: t('BONBOZ.THANH_TREN.BAO_CAO'),
      icon: 'i-lucide-chart-no-axes-column',
      toi: {
        name: 'account_overview_reports',
        params: { accountId: accountId.value },
      },
      thuoc: [`${goc}/reports`],
      // Cùng bộ quyền mà `reports.routes.js` đang chặn ở route. Không lọc ở
      // đây thì nhân viên vẫn thấy nút, bấm vào là đụng tường — nút dẫn tới
      // chỗ không vào được còn tệ hơn là không có nút.
      quyen: ['administrator', 'report_manage'],
    },
    {
      ten: 'crm',
      chiAdmin: true,
      nhan: t('BONBOZ.THANH_TREN.CRM'),
      icon: 'i-lucide-contact',
      toi: { name: 'bonboz_crm_index', params: { accountId: accountId.value } },
      thuoc: [`${goc}/crm`],
    },
    {
      ten: 'cai-dat',
      chiAdmin: true,
      nhan: t('BONBOZ.THANH_TREN.CAI_DAT'),
      icon: 'i-lucide-settings',
      toi: { name: 'settings_home', params: { accountId: accountId.value } },
      thuoc: [`${goc}/settings`, `${goc}/profile`],
      // Cài đặt KHÔNG lọc: nhân viên vẫn có Cài đặt cá nhân, Câu trả lời mẫu
      // và Macro ở trong đó. Việc ẩn từng mục con làm ở `MenuTrangCon.vue`.
    },
  ].filter(m => !m.quyen || checkPermissions(m.quyen));
});

// Nguoi truc chi thay Hoi thoai. Bao cao / CRM / Cai dat deu bi route guard
// chan roi, hien nut ra chi de bam vao rot ve dashboard khong loi giai thich.
const MUC_HIEN = computed(() =>
  CAC_MUC.value.filter(muc => isAdmin.value || !muc.chiAdmin)
);

const dangO = muc => muc.thuoc.some(duong => route.path.startsWith(duong));

// Sanh cho = man chon trang, chua co ngu canh trang nao.
const oSanhCho = computed(() => route.name === 'bonboz_main_page');

// Co bay dai dieu huong khong.
//
// KHONG bay o sanh cho, va khong bay o man thong bao NEU minh vua tu sanh cho
// bam sang (xem `?ve=chon-trang` o tren). Hai lan sua truoc deu lech mot nua:
// bay o moi noi thi tu sanh cho bam chuong la thay bon nut hien ra nhu the da
// chon trang; giau o moi noi thi dang trong mot trang bam chuong la mat duong
// ve. Ngu canh nao giu ngu canh do.
const oTrangChinh = computed(
  () =>
    oSanhCho.value ||
    (route.name === 'notifications_index' && route.query.ve === 'chon-trang')
);

const globalConfig = useMapGetter('globalConfig/get');
// Logo doi ban theo chu de (chu teal / kem / toi, xem helper/chuDe.js) — doc
// `chuDeHienTai` chu khong doc uiSettings de logo doi cung nhip voi nen, khong
// cho luu xong. Logo tuy chinh qua super admin (ngoai brand-assets) giu nguyen.
const logo = computed(() => {
  const tuyChinh = globalConfig.value.logo;
  if (tuyChinh && !tuyChinh.includes('/brand-assets/')) return tuyChinh;
  return timChuDe(chuDeHienTai.value).logo;
});

// Chuong chi phat su kien, khong tu dieu huong — Chatwoot de ben goi quyet dinh
// mo panel hay chuyen trang. BonBoZ chua co panel thong bao nen chuyen trang.
// Bam chuong TU SANH CHO thi danh dau lai bang `?ve=chon-trang`.
//
// Man thong bao GIU NGUYEN ngu canh noi minh di toi: dang trong app thi van du
// dai dieu huong (khong thi mat duong ve Hoi thoai/Thong ke/CRM/Cai dat); dang
// o sanh cho — tuc chua chon trang nao — thi khong bay dai do ra, vi nhu vay la
// noi doi rang da co ngu canh trang. Quay lai sanh bang logo, dung nhu moi man
// khac cua sanh.
//
// Dat o QUERY chu khong phai `history.state`: nguoi dung tai lai trang
// /notifications thi van dung, va nhin URL la biet minh dang o nhanh nao.
const moTrangThongBao = () =>
  router.push({
    name: 'notifications_index',
    params: { accountId: accountId.value },
    query: oSanhCho.value ? { ve: 'chon-trang' } : {},
  });

// Bấm logo về trang chính — cùng chỗ đăng nhập xong rơi vào.
const veTrangChinh = computed(() =>
  frontendURL(`accounts/${accountId.value}/pages`)
);
</script>

<template>
  <!-- BonBoZ: navbar #506DAD cao 56px de nhan vien tu Pancake sang quen mat. -->
  <!-- `ps-2 md:ps-0`: tren dien thoai logo sat rat mep man. Tu 768px tro len
       KHONG dem trai — o logo bi ghim 424px de tam no trung tam cot hoi thoai
       (xem <style> duoi), them dem vao la lech. -->
  <header
    class="flex gap-2 items-center h-14 bonboz-chrome ps-2 pe-3 md:ps-0 shrink-0"
  >
    <RouterLink
      :to="veTrangChinh"
      class="o-logo flex items-center justify-center min-w-0 md:shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[rgb(var(--bonboz-chu-khung))]"
      :aria-label="$t('BONBOZ.THANH_TREN.TRANG_CHINH')"
    >
      <!-- Vet sang khi ro chuot chi om quanh logo. Ca the <a> phai rong bang
           cot hoi thoai de canh giua logo, nen to nguyen the la vet sang trai
           dai gan nua man hinh. -->
      <span
        class="flex items-center min-w-0 px-2 sm:px-2.5 py-1.5 rounded-lg transition-colors hover:bg-[rgb(var(--bonboz-chu-khung)/15%)]"
      >
        <!-- `max-w-full object-contain`: man hep thi logo TU THU NHO chu khong
             bi cat hay day nav di. `h-7` duoi 640px de doi lai vai pixel be
             ngang cho bon muc dieu huong. -->
        <img
          :src="logo"
          alt="BonBoZ"
          class="object-contain w-auto max-w-full h-7 sm:h-8"
        />
      </span>
    </RouterLink>

    <!-- BonBoZ: `shrink-0` chu KHONG `min-w-0`. Truoc day nav la phan co lai:
         tren man 320-360px no bi ep hep hon noi dung, cac muc tran ra ngoai va
         chui xuong duoi cum chuong/avatar — do duoc 11/09/2026: o 320px "Cai
         dat" chong len chuong 47px, o 360px chong 7px. Gio phan co lai la o
         logo (no biet tu thu nho), nav va cum phai luon du cho. -->
    <nav
      v-if="!oTrangChinh"
      class="flex gap-1 items-center mx-auto sm:gap-2 shrink-0 md:mx-0"
    >
      <RouterLink
        v-for="muc in MUC_HIEN"
        :key="muc.ten"
        :to="muc.toi"
        :aria-label="muc.nhan"
        :title="muc.nhan"
        class="relative flex items-center gap-1.5 h-8 px-2 sm:px-3 rounded-lg text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
        :class="
          dangO(muc)
            ? 'bg-[rgb(var(--bonboz-chu-khung)/20%)] font-medium'
            : 'text-[rgb(var(--bonboz-chu-khung)/75%)] hover:bg-[rgb(var(--bonboz-chu-khung)/10%)]'
        "
      >
        <span :class="muc.icon" class="size-4 shrink-0" aria-hidden="true" />
        <!-- Duoi 640px chi con bieu tuong: bon nhan tieng Viet khong vua man
             dien thoai. Nhan van o `aria-label`/`title` nen doc man hinh va
             chuot van doc duoc.
             AN LAI TU 768 DEN 1023px: tu 768 o logo bi ghim 424px (can giua cot
             hoi thoai, xem <style> duoi), cong bon nhan la 864px — o may bang
             768px thi chuong thong bao bi day HAN ra ngoai man, khong bam duoc
             ma cung khong co thanh cuon nao bao. Do duoc 11/09/2026: tran phai
             112px. Tu 1024 tro len lai du cho cho ca nhan. -->
        <span class="hidden truncate sm:inline md:hidden lg:inline">
          {{ muc.nhan }}
        </span>
        <!-- Tong so hoi thoai chua doc, dat goc tren phai nhu Pancake. -->
        <UnreadBadge
          v-if="muc.ten === 'hoi-thoai'"
          :count="soChuaDoc"
          class="absolute -top-1 -right-1 ring-1 ring-[rgb(var(--pk-chrome))]"
        />
      </RouterLink>
    </nav>

    <!-- Le tu dong: khi CO <nav> thi hai le `mx-auto` cua no da nuot het cho
         thua, cum nay tu nam sat mep phai — them mot le tu dong nua o day la
         cho thua chia lam ba va nav lech sang trai. Nhung TRANG CHINH khong ve
         <nav>, luc do khong con le tu dong nao ca va cum nay dinh luon vao
         logo, nen phai tu day sang phai. -->
    <div
      class="flex gap-2 items-center shrink-0"
      :class="oTrangChinh ? 'ms-auto' : 'md:ms-auto'"
    >
      <!-- BonBoZ: chuong thong bao. Component nay co san trong Chatwoot nhung
           KHONG duoc mount o dau ca — thanh trai da bo no di, thanh tren thi
           chua bao gio co. Ket qua: trang /notifications chi vao duoc bang cach
           go dia chi. Voi PWA vua co thong bao day thi day la cho nguoi ta bam
           vao sau khi nhan duoc bao. Hien o MOI khổ man, khong rieng dien
           thoai — may tinh cung dang thieu. -->
      <!-- BonBoZ: cum trang dang gop. Hien o MOI man, ke ca Bang dieu khien —
           o do no la duong nhanh nhat de nhay thang vao mot trang ma khong phai
           do tim trong luoi the (nguoi dung yeu cau 17/09/2026).
           AN duoi 768px: do la nguong thanh trai bi an han, luc do o day da co
           menu ho so, va hai cum avatar canh nhau tren man dien thoai thi khong
           con cho cho <nav>. -->
      <MergedPagesMenu class="hidden md:block" />

      <ChuongThongBao
        class="text-[rgb(var(--bonboz-chu-khung)/85%)] hover:bg-[rgb(var(--bonboz-chu-khung)/10%)]"
        @open-notification-panel="moTrangThongBao"
      />

      <!-- BonBoZ: menu ho so CHI tren dien thoai. Tu 768px tro len no nam duoi
           chan thanh trai nhu cu; duoi nguong do thanh trai bi an han (thay doi
           cung dot), nen doi trang thai truc/nghi va Dang xuat phai co cho khac.
           `is-collapsed` de chi hien avatar, khong hien ten + email. -->
      <div class="md:hidden">
        <SidebarProfileMenu
          is-collapsed
          mo-xuong
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Logo can giua cot danh sach hoi thoai: o logo rong gap doi tam cot
   (rail 56px + nua be ngang cot), nen tam o trung voi tam cot.
   Ba so nay bam theo w-[312px] 2xl:w-[352px] 3xl:w-[392px] cua ChatList.vue —
   doi be ngang cot ben do thi sua ca ba so nay.
   Viet o day thay vi class Tailwind arbitrary: breakpoint 3xl la breakpoint
   rieng cua repo, de bi drop im lang khi build.

   CHI tu 768px tro len. Duoi nguong do khong con hai cot de can, ma 392px thi
   rong hon ca man dien thoai: o logo day <nav> ra ngoai viewport, `min-w-0` ep
   nav ve width 0, va bon muc dieu huong bien mat hoan toan. Vi thanh trai da an
   het muc dieu huong (helper/sidebarVisibility.js), day la duong DUY NHAT toi
   Thong ke/CRM/Cai dat -> mat luon. Do duoc ngay 09/09/2026: nav width 0 tai
   left 400 tren man 390px. */
@media (min-width: 768px) {
  .o-logo {
    width: 424px;
  }
}
@media (min-width: 1536px) {
  .o-logo {
    width: 464px;
  }
}
@media (min-width: 1900px) {
  .o-logo {
    width: 504px;
  }
}
</style>
